import { ApiClient, ApiResponse } from '../client';
import { Cart } from '../models';

// Verificación de si estamos en el navegador o en el servidor
const isBrowser = typeof window !== 'undefined';

/**
 * Servicio para gestionar el carrito de compras
 */
export class CartService {
  private client: ApiClient;
  private guestId: string = '';

  constructor(client: ApiClient) {
    this.client = client;

    // Solo acceder a localStorage en el navegador
    if (isBrowser) {
      // Intentar recuperar guestId de localStorage para usuarios anónimos
      const storedGuestId = localStorage.getItem('guest_id');

      // Si no existe, generar uno nuevo
      if (!storedGuestId) {
        this.guestId = this.generateGuestId();
        localStorage.setItem('guest_id', this.guestId);
      } else {
        this.guestId = storedGuestId;
      }
    } else {
      // En el servidor, generar un ID temporal
      this.guestId = this.generateGuestId();
    }
  }

  /**
   * Obtener el contenido del carrito actual
   */
  public async getCart(): Promise<ApiResponse<Cart>> {
    console.log('🛒 [CartService] getCart called, authToken:', !!this.client.getAuthToken(), 'guestId:', this.guestId);
    // Si hay un usuario autenticado, obtener su carrito
    if (this.client.getAuthToken()) {
      console.log('🛒 [CartService] Fetching cart for authenticated user');
      return await this.client.get<Cart>('/products/cart', { useCache: false, cache: 'no-store' });
    } else {
      // Para usuarios anónimos, incluir el guestId
      console.log('🛒 [CartService] Fetching cart for guest with guestId:', this.guestId);
      return await this.client.get<Cart>('/products/cart', {
        params: { guestId: this.guestId },
        useCache: false,
        cache: 'no-store'
      });
    }
  }

  /**
   * Añadir un producto al carrito
   * @param productId ID del producto a añadir
   * @param quantity Cantidad a añadir (por defecto 1)
   * @param variantSku SKU de la variante (opcional)
   */
  public async addToCart(productId: string, quantity: number = 1, variantSku?: string): Promise<ApiResponse<Cart>> {
    const data: any = {
      productId,
      quantity
    };

    // Añadir variante si existe
    if (variantSku) {
      data.variantSku = variantSku;
    }

    // Si no hay usuario autenticado, incluir el guestId
    if (!this.client.getAuthToken()) {
      data.guestId = this.guestId;
    }

    return await this.client.post<Cart>('/products/cart', data);
  }

  /**
   * Actualizar la cantidad de un producto en el carrito
   * @param productId ID del producto a actualizar
   * @param quantity Nueva cantidad
   */
  public async updateCartItem(productId: string, quantity: number, variantSku?: string): Promise<ApiResponse<Cart>> {
    const data: any = {
      quantity
    };

    if (variantSku) {
      data.variantSku = variantSku;
    }

    // Si no hay usuario autenticado, incluir el guestId
    if (!this.client.getAuthToken()) {
      data.guestId = this.guestId;
    }

    return await this.client.put<Cart>(`/products/cart/${productId}`, data);
  }

  /**
   * Eliminar un producto del carrito
   * @param productId ID del producto a eliminar
   */
  public async removeFromCart(productId: string, variantSku?: string): Promise<ApiResponse<Cart>> {
    // Para usuarios anónimos, incluir el guestId en los parámetros
    const params: any = !this.client.getAuthToken()
      ? { guestId: this.guestId }
      : {};

    if (variantSku) {
      params.variantSku = variantSku;
    }

    return await this.client.delete<Cart>(`/products/cart/${productId}`, {
      params
    });
  }

  /**
   * Vaciar completamente el carrito
   */
  public async clearCart(): Promise<ApiResponse<Cart>> {
    // Para usuarios anónimos, incluir el guestId en los parámetros
    const params = !this.client.getAuthToken()
      ? { guestId: this.guestId }
      : undefined;

    return await this.client.delete<Cart>('/products/cart', {
      params
    });
  }

  /**
   * Migrar un carrito de invitado a un usuario autenticado
   * Útil después de un login para preservar productos añadidos antes de autenticarse
   */
  public async migrateCart(): Promise<ApiResponse<Cart>> {
    // Solo tiene sentido si hay un usuario autenticado y un guestId
    if (this.client.getAuthToken() && this.guestId) {
      const response = await this.client.post<Cart>('/products/cart/migrate', {
        guestId: this.guestId
      });

      // Si la migración es exitosa, generar un nuevo guestId
      if (response.success && isBrowser) {
        this.guestId = this.generateGuestId();
        localStorage.setItem('guest_id', this.guestId);
      }

      return response;
    }

    return {
      success: false,
      error: {
        code: 'NOT_AUTHENTICATED',
        message: 'El usuario debe estar autenticado para migrar el carrito'
      }
    };
  }

  /**
   * Verificar si el carrito está vacío
   */
  public async isCartEmpty(): Promise<boolean> {
    const response = await this.getCart();
    return !response.success || !response.data || !response.data.items || response.data.items.length === 0;
  }

  /**
   * Obtener el número total de items en el carrito
   */
  public async getCartItemsCount(): Promise<number> {
    const response = await this.getCart();
    return response.success && response.data ? response.data.totalItems : 0;
  }

  /**
   * Calcular el subtotal del carrito
   */
  public async getCartSubtotal(): Promise<number> {
    const response = await this.getCart();
    return response.success && response.data ? response.data.subtotal : 0;
  }

  /**
   * Generar un ID único para usuarios invitados
   */
  private generateGuestId(): string {
    return 'guest_' +
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15) +
      '_' + Date.now().toString(36);
  }
}
